import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { isNativeShell } from '../../utils/distribution';

export interface ResponsiveScrollTabItem {
  value: string;
  label: string;
  shortLabel?: string;
  /** Label shown in native mobile select menu (defaults to shortLabel, then label). */
  selectLabel?: string;
}

interface ResponsiveScrollTabsProps {
  tabs: ResponsiveScrollTabItem[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  /** Visible label on native select (defaults to ariaLabel). */
  selectLabel?: string;
  nativeUseSelect?: boolean;
}

const SELECT_ID = 'responsive-scroll-tabs-select';

function tabOptionLabel(tab: ResponsiveScrollTabItem): string {
  return tab.selectLabel ?? tab.shortLabel ?? tab.label;
}

function buildSelectGuidanceLabel(tabs: ResponsiveScrollTabItem[], fieldLabel: string): string {
  if (tabs.length <= 3) {
    return `Select: ${tabs.map(tabOptionLabel).join(', ')}`;
  }
  return fieldLabel;
}

const ResponsiveScrollTabs: React.FC<ResponsiveScrollTabsProps> = ({
  tabs,
  value,
  onChange,
  ariaLabel = 'Section navigation',
  selectLabel,
  nativeUseSelect = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isPhoneLandscape = useMediaQuery('(orientation: landscape) and (max-height: 520px)');
  const nativeShell = isNativeShell();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showRightFade, setShowRightFade] = useState(false);
  const fieldLabel = selectLabel ?? ariaLabel;

  const updateFade = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      setShowRightFade(false);
      return;
    }

    const scroller = container.querySelector('.MuiTabs-scroller') as HTMLElement | null;
    if (!scroller) {
      setShowRightFade(false);
      return;
    }

    const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 4;
    const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4;
    setShowRightFade(hasOverflow && !atEnd);
  }, []);

  useEffect(() => {
    updateFade();
    window.addEventListener('resize', updateFade);

    const container = scrollRef.current;
    const scroller = container?.querySelector('.MuiTabs-scroller') as HTMLElement | null;
    scroller?.addEventListener('scroll', updateFade, { passive: true });

    return () => {
      window.removeEventListener('resize', updateFade);
      scroller?.removeEventListener('scroll', updateFade);
    };
  }, [tabs, updateFade, value]);

  if (nativeShell && nativeUseSelect && (isMobile || isPhoneLandscape)) {
    const guidanceLabel = buildSelectGuidanceLabel(tabs, fieldLabel);
    const selectedTab = tabs.find((tab) => tab.value === value);

    return (
      <Box sx={{ width: '100%' }}>
        <Typography
          variant="caption"
          component="label"
          htmlFor={SELECT_ID}
          sx={{
            display: 'block',
            mb: 0.5,
            color: 'text.secondary',
            lineHeight: 1.35,
            fontWeight: 500,
          }}
        >
          {guidanceLabel}
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            id={SELECT_ID}
            value={value}
            onChange={(event) => onChange(String(event.target.value))}
            displayEmpty={false}
            renderValue={() => (selectedTab ? tabOptionLabel(selectedTab) : '')}
            inputProps={{ 'aria-label': fieldLabel }}
            MenuProps={{
              disablePortal: false,
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
              },
            }}
          >
            {tabs.map((tab) => (
              <MenuItem key={tab.value} value={tab.value}>
                {tabOptionLabel(tab)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
      }}
    >
      <Tabs
        value={value}
        onChange={(_, nextValue) => onChange(String(nextValue))}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label={ariaLabel}
        sx={{
          minHeight: 48,
          '& .MuiTab-root': {
            minHeight: 48,
            px: { xs: 1.25, sm: 2 },
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              <>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {tab.label}
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {tab.shortLabel ?? tab.label}
                </Box>
              </>
            }
          />
        ))}
      </Tabs>

      {showRightFade && (
        <Box
          aria-hidden
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 32,
            background: (muiTheme) =>
              `linear-gradient(to right, transparent, ${muiTheme.palette.background.paper})`,
          }}
        />
      )}
    </Box>
  );
};

export default ResponsiveScrollTabs;
